import { useCallback, memo, useMemo } from 'react';
import {
  ReactHookFormAutocomplete,
  Button,
  Grid,
  Popper,
  PopperActions,
  PopperContent,
  PopperTitle,
  Typography,
  useTranslations,
  usePubSub,
  buildFilterOptionsAsObject,
  getSecurityScoreOptions,
  ISecurityScoreOption,
  TSecurityScoreName,
} from '@uniqkey-frontend/shared-app';
import { useForm } from 'react-hook-form';
import PubSubEventEnum from '../../../../enums/PubSubEventEnum';

export interface IServicesListFilterSubmitResult {
  securityScoreName: TSecurityScoreName;
}

interface IServicesListFilterProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onSubmit: (result: IServicesListFilterSubmitResult) => void;
  onClose: () => void;
  initialValues: IServicesListFilterSubmitResult;
}

interface IFormValues {
  securityScore: ISecurityScoreOption | null;
}

const POPPER_MIN_WIDTH = { minWidth: 500 };

const ServicesListFilter = (props: IServicesListFilterProps) => {
  const {
    isOpen, anchorEl, onSubmit, onClose, initialValues,
  } = props;
  const { t } = useTranslations();

  const [securityScoreOptions, securityScoreOptionsAsObject] = useMemo(() => {
    const options = getSecurityScoreOptions(t);
    const optionsAsObject = buildFilterOptionsAsObject(options);
    return [options, optionsAsObject];
  }, [t]);

  const {
    handleSubmit, control, reset, setValue, formState: { isDirty },
  } = useForm<IFormValues>({
    defaultValues: {
      securityScore: securityScoreOptionsAsObject[
        initialValues.securityScoreName as keyof typeof securityScoreOptionsAsObject
      ] ?? null,
    },
  });

  const handleOnSubmit = useCallback((value: IFormValues): void => {
    const { securityScore } = value;
    onSubmit({
      securityScoreName: securityScore?.value,
    });
    onClose();
    reset(value); // set default values to the selected ones to reset isDirty
  }, [reset, onSubmit, onClose]);

  const clearAll = useCallback(() => {
    setValue('securityScore', null, { shouldDirty: true });
  }, [setValue]);

  const handleOnFadeExited = useCallback(() => {
    reset(); // reset form values when Popper is closed and animation has finished
  }, [reset]);

  const handleReset = useCallback(() => {
    reset({
      securityScore: null,
    });
  }, [reset]);
  usePubSub(PubSubEventEnum.RESET_FILTER, handleReset);

  return (
    <Popper
      anchorEl={anchorEl}
      offsetX={-8}
      offsetY={16}
      placement="bottom-start"
      open={isOpen}
      sx={POPPER_MIN_WIDTH}
      onFadeExited={handleOnFadeExited}
    >
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <PopperTitle onClose={onClose}>
          {t('common.filterBy')}
        </PopperTitle>
        <PopperContent>
          <Grid container alignItems="center">
            <Grid item xs={3}>
              <Typography>
                {t('servicesFilter.securityScore.label')}
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <ReactHookFormAutocomplete
                name="securityScore"
                options={securityScoreOptions}
                placeholder={t('servicesFilter.securityScore.placeholder')}
                control={control}
              />
            </Grid>
          </Grid>
        </PopperContent>
        <PopperActions>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button disabled={!isDirty} fullWidth type="submit">{t('common.apply')}</Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearAll}
              >
                {t('common.clearAll')}
              </Button>
            </Grid>
          </Grid>
        </PopperActions>
      </form>
    </Popper>
  );
};

export default memo(ServicesListFilter);
